/**
 * AURA Toggle Switch Control
 */

export interface ToggleOptions {
  id: string;
  label: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
}

const TOGGLE_STYLES = `
  .aura-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--aura-spacing-sm) 0;
    min-height: var(--aura-touch-target);
  }

  .aura-toggle__label {
    font-size: var(--aura-font-size-md);
    color: var(--aura-text);
    cursor: pointer;
    flex: 1;
    padding-right: var(--aura-spacing-md);
  }

  .aura-toggle__switch {
    position: relative;
    width: 48px;
    height: 24px;
    flex-shrink: 0;
  }

  .aura-toggle__input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    margin: 0;
    z-index: 2;
  }

  .aura-toggle__track {
    position: absolute;
    inset: 0;
    background: var(--aura-border);
    border-radius: var(--aura-radius-full);
    transition: background-color var(--aura-transition-fast);
  }

  .aura-toggle__thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 20px;
    height: 20px;
    background: white;
    border-radius: var(--aura-radius-full);
    box-shadow: var(--aura-shadow-sm);
    transition: transform var(--aura-transition-fast);
  }

  .aura-toggle__input:checked + .aura-toggle__track {
    background: var(--aura-primary);
  }

  .aura-toggle__input:checked + .aura-toggle__track .aura-toggle__thumb {
    transform: translateX(24px);
  }

  .aura-toggle__input:focus-visible + .aura-toggle__track {
    outline: 2px solid var(--aura-border-focus);
    outline-offset: 2px;
  }
`;

export function createToggle(options: ToggleOptions): { element: HTMLElement; styles: string } {
  const container = document.createElement('div');
  container.className = 'aura-toggle';

  const label = document.createElement('label');
  label.className = 'aura-toggle__label';
  label.setAttribute('for', options.id);
  label.textContent = options.label;

  const switchWrapper = document.createElement('div');
  switchWrapper.className = 'aura-toggle__switch';

  const input = document.createElement('input');
  input.type = 'checkbox';
  input.id = options.id;
  input.className = 'aura-toggle__input';
  input.checked = options.checked || false;
  input.setAttribute('role', 'switch');
  input.setAttribute('aria-checked', String(input.checked));

  const track = document.createElement('div');
  track.className = 'aura-toggle__track';
  track.setAttribute('aria-hidden', 'true');

  const thumb = document.createElement('div');
  thumb.className = 'aura-toggle__thumb';

  track.appendChild(thumb);
  switchWrapper.appendChild(input);
  switchWrapper.appendChild(track);
  container.appendChild(label);
  container.appendChild(switchWrapper);

  input.addEventListener('change', () => {
    input.setAttribute('aria-checked', String(input.checked));
    options.onChange?.(input.checked);
  });

  return { element: container, styles: TOGGLE_STYLES };
}

export { TOGGLE_STYLES };
